import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tag } from "antd";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { TableC } from "@/CommonElements/TableC";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useNusukCardsLookupPilgrimsMutation } from "@/services/nusukCards/nusukCards";
import { DATE_TIME_FORMAT } from "@/utils/constants";

const PAGE_SIZE = 15;

export const NusukCardsInquiryTable = ({ lookupRequest, allContracts }) => {
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();
  const { mutate: lookupPilgrims, isPending } =
    useNusukCardsLookupPilgrimsMutation();

  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const { searchValue, debouncedValue, setSearchValue } =
    useDebouncedSearch("", 500);

  // ─── Derived from API response ─────────────────────────────────────

  const pilgrims = data?.pilgrims ?? [];
  const pagination = data?.pagination ?? {};
  const currentPage = Number(pagination.current_page) || page;
  const pageSize = Number(pagination.per_page) || PAGE_SIZE;
  const totalRows = Number(pagination.total) || 0;

  // ─── Fetch data from API ───────────────────────────────────────────

  const fetchData = useCallback(
    (targetPage = 1) => {
      if (!lookupRequest?.type || !lookupRequest?.number) return;

      lookupPilgrims(
        {
          type: lookupRequest.type,
          number: lookupRequest.number,
          page: targetPage,
          per_page: PAGE_SIZE,
          search: debouncedValue?.trim() || undefined,
          ...filters,
        },
        {
          onSuccess: (res) => {
            const result = res?.data?.data || res?.data || {};
            setData(result);
            setPage(
              Number(result?.pagination?.current_page) || targetPage
            );
          },
          onError: () =>
            toast.error(
              t("NO_RESULTS_FOUND", { defaultValue: t("NO_RESULTS") })
            ),
        }
      );
    },
    [lookupRequest, debouncedValue, filters, lookupPilgrims, t]
  );

  useEffect(() => {
    if (!lookupRequest?.number) {
      setData(null);
      return;
    }
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupRequest, debouncedValue, filters]);

  // ─── Handlers ──────────────────────────────────────────────────────

  const handlePageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);
      fetchData(nextPage);
    },
    [fetchData]
  );

  const handleSearchChange = useCallback(
    (e) => {
      setSearchValue(e.target.value);
      setPage(1);
    },
    [setSearchValue]
  );

  const handleFilterApply = useCallback((next) => {
    setFilters(next);
    setPage(1);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  // ─── Column definitions ────────────────────────────────────────────

  const columns = useMemo(
    () => [
      {
        key: "index",
        name: t("SERIAL_NUMBER", { defaultValue: "م" }),
        render: (_, _row, index) =>
          (currentPage - 1) * pageSize + index + 1,
      },
      {
        key: "full_name_en",
        name: t("PILGRIM_NAME"),
        render: (_, row) => row?.full_name_en || row?.full_name_ar || "-",
      },
      {
        key: "border_number",
        name: t("PILGRIM_NUMBER"),
        render: (_, row) => row?.border_number || "-",
      },
      {
        key: "union_no",
        name: t("NUSUK_CARD_NUMBER"),
        render: (_, row) => row?.union_no || "-",
      },
      {
        key: "nationality",
        name: t("NATIONALITY"),
        render: (_, row) =>
          row?.nationality
            ? getLocalizedText(
                row.nationality.name_ar,
                row.nationality.name_en
              ) || row.nationality.name
            : row?.nationality_name || "-",
      },
      {
        key: "passport_no",
        name: t("PASSPORT_NUMBER"),
        render: (_, row) => row?.passport_no || "-",
      },
      {
        key: "card_status",
        name: t("CARD_STATUS"),
        render: (_, row) => {
          const statusId = row?.nusuk_card?.card_status_id;
          const statusLabel = row?.nusuk_card?.card_status;
          if (statusId == null && statusLabel == null) return "-";

          const isActive =
            statusId === "active" ||
            statusId === 1 ||
            statusId === "1" ||
            String(statusLabel).toLowerCase() === "active";
          const isInactive =
            statusId === "inactive" ||
            statusId === 0 ||
            statusId === "0" ||
            String(statusLabel).toLowerCase() === "inactive";

          if (isActive) return <Tag color="success">{t("NUSUK_ACTIVE")}</Tag>;
          if (isInactive) return <Tag color="error">{t("NUSUK_INACTIVE")}</Tag>;
          return <Tag>{statusId || statusLabel || "-"}</Tag>;
        },
      },
      {
        key: "activation_date",
        name: t("ACTIVATION_DATE"),
        render: (_, row) =>
          row?.nusuk_card?.activation_date
            ? getLocalizedDate(
                row.nusuk_card.activation_date,
                DATE_TIME_FORMAT
              )
            : "-",
      },
    ],
    [t, getLocalizedText, getLocalizedDate, currentPage, pageSize]
  );

  // ─── Filter field definitions ──────────────────────────────────────

  const filterFields = useMemo(
    () => [
      {
        name: "campaign_group_number",
        label: t("CAMPAIGN_OR_GROUP_NUMBER"),
        type: "input",
      },
      {
        name: "service_center_id",
        label: t("SERVICE_CENTER"),
        type: "select",
        optionKey: "service_centers",
        options: (s) => ({ value: s.id, label: s.name }),
      },
      {
        name: "organizer",
        label: t("ORGANIZER"),
        type: "select",
        optionKey: "missions",
        options: (m) => ({
          value: m.id,
          label: getLocalizedText(m.name_ar, m.name_en) || m.name,
        }),
      },
      {
        name: "subordinate_organizer",
        label: t("SUBORDINATE_ORGANIZER"),
        type: "input",
      },
      {
        name: "group_name",
        label: t("GROUP_NAME"),
        type: "input",
      },
      {
        name: "nationality_id",
        label: t("NATIONALITY"),
        type: "select",
        optionKey: "nationalities",
        options: (n) => ({
          value: n.id,
          label: getLocalizedText(n.name_ar, n.name_en) || n.name,
        }),
      },
      {
        name: "status",
        label: t("CARD_STATUS"),
        type: "select",
        options: [
          { value: "all", label: t("NUSUK_ALL_STATUSES") },
          { value: "active", label: t("NUSUK_ACTIVE") },
          { value: "inactive", label: t("NUSUK_INACTIVE") },
        ],
      },
      {
        name: "activation_date",
        label: t("ACTIVATION_DATE"),
        type: "date",
      },
      {
        name: "request_number",
        label: t("ARRIVAL_CARD_REQUEST_NUMBER"),
        type: "input",
      },
      {
        name: "housing_contract_no",
        label: t("HOUSING_CONTRACT_NO"),
        type: "select",
        options: (allContracts ?? []).map((c) => ({
          value: String(c.id),
          label: c.house_commercial_name_ar
            ? `${c.id} - ${c.house_commercial_name_ar}`
            : String(c.id),
        })),
      },
    ],
    [t, getLocalizedText, allContracts]
  );

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <TableC
      data={pilgrims}
      columns={columns}
      loading={isPending}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      searchPlaceholder={t("SEARCH_HERE")}
      pagination={{
        current: currentPage,
        total: totalRows,
        pageSize,
        onChange: handlePageChange,
      }}
      filterFields={filterFields}
      filters={filters}
      onFilterApply={handleFilterApply}
      onFilterReset={handleFilterReset}
      emptyStateTitle={t("NUSUK_INQUIRY_NO_RESULTS")}
      emptyStateDescription={t("NUSUK_INQUIRY_NO_RESULTS_DESC")}
    />
  );
};
